import './BusinessSettings.css'
import 'components/shortlist/actions/save/index.css'
import cn from 'clsx'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect, type ConnectedProps } from 'react-redux'
import { MdCloudUpload as CloudUpload } from '@react-icons/all-files/md/MdCloudUpload'
import { Link } from 'react-router-dom'
import type { DropdownStringOption } from 'types/ui'
import Intercom from '@disco/api/intercom'
import type { BusinessTypes } from '@disco/constants/business-type-v2'
import {
  BUSINESS_TYPES,
  BUSINESS_TYPES_LABEL_MAP,
} from '@disco/constants/business-type-v2'
import {
  CUSTOM_DOWNLOAD_FILE_NAMES,
  TRANSCODE_320K,
  IS_SUBSCRIBER,
  IS_ARTIST,
} from '@disco/constants/FeatureTypes'
import {
  getFeatureData,
  type featureData,
} from '@disco/state-management/selectors/business'
import { getMultiLevelSetting } from '@disco/state-management/selectors/user'
import FileSelect from 'components/common/file-select'
import RichTextEditor from 'components/common/rich-text-editor'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import ContactUsLink from 'components/ui/contact-us-link'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Icon from 'components/ui/icon'
import InlineError from 'components/ui/inline-error'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'
import TextField from 'components/ui/text-field'
import FileDropWrapper from 'containers/FileDropWrapper'
import MetadataHoverSettings from 'pages/settings/display/metadata-hover-settings'
const ZIP_NUMBER = 'zip_number'
import type { UserReducer } from '@disco/state-management/reducers/user'
import type { RootState } from 'store/configureStore'
type OwnProps = {
  user: UserReducer
  updateBusinessSettings: (...args: Array<any>) => any
  loadBusinessSettings: (...args: Array<any>) => any
  sendMixpanelEvent: (...args: Array<any>) => any
  businessSettings: Record<string, any>
  featureData: Record<string, featureData>
  businessType: BusinessTypes
}
type TrackDownloadFilenameOption = DropdownStringOption & {
  badge?: React.ReactNode
  disabled?: boolean
  valueContainer?: React.ReactNode
}
const mapStateToProps = (state: RootState) => {
  const subscriberData = getFeatureData(state, {
    featureName: IS_SUBSCRIBER,
  })
  const artistData = getFeatureData(state, {
    featureName: IS_ARTIST,
  })
  return {
    isSubscriber: Boolean(subscriberData?.isFeatureEnabled),
    isArtist: Boolean(artistData?.isFeatureEnabled),
  }
}
const connector = connect(mapStateToProps)
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & OwnProps

class BusinessSettings extends React.Component<
  Props,
  {
    address: Record<string, any> | null | undefined
    name: string
    addressMjml: string | null | undefined
    explorer_trackhover_fields: string[]
    explorer_trackpane_fields: string[]
    imageFile: File | null | undefined
    image: string | ArrayBuffer | null | undefined
    attach_writers_to_metadata: boolean
    write_native_tags_to_comments_metadata_enabled: boolean
    auto_email_shared_playlist_passwords: boolean
    trackDownloadFilename: TrackDownloadFilenameOption[]
    transcoding: string
    zipNumber: boolean
    businessType: BusinessTypes
    invalidBusinessDataMessage?: string
  }
> {
  state: {
    address: any | null | undefined
    addressMjml: string | null | undefined
    attach_writers_to_metadata: boolean
    auto_email_shared_playlist_passwords: boolean
    explorer_trackhover_fields: Array<string>
    explorer_trackpane_fields: Array<string>
    image: string | null | undefined
    imageFile: File | null | undefined
    name: string
    trackDownloadFilename: Array<TrackDownloadFilenameOption>
    transcoding: string
    write_native_tags_to_comments_metadata_enabled: boolean
    zipNumber: boolean
    businessType: BusinessTypes | null
    invalidBusinessDataMessage?: string
  } = {
    name: this.props.user.business.name || '',
    image: this.props.user.business.image,
    address: this.props.user.business.business_address,
    addressMjml: this.props.user.business.business_address_mjml,
    explorer_trackhover_fields: getMultiLevelSetting.explorer_trackhover_fields(
      this.props.user,
    ).settingBusiness,
    explorer_trackpane_fields: getMultiLevelSetting.explorer_trackpane_fields(
      this.props.user,
    ).settingBusiness,
    // imageFile has 3 possible values:
    // - undefined (keep image)
    // - null (remove image)
    // - blob (add image)
    imageFile: undefined,
    attach_writers_to_metadata:
      this.props.user.business.attach_writers_to_metadata,
    write_native_tags_to_comments_metadata_enabled:
      this.props.user.business.write_native_tags_to_comments_metadata_enabled,
    auto_email_shared_playlist_passwords:
      this.props.user.business.auto_email_shared_playlist_passwords,
    trackDownloadFilename: [],
    transcoding: this.props.user.business.audio_transcode_preset,
    zipNumber: false,
    businessType: this.props.businessType,
  }

  getUncompiledJson(): Array<any | string> {
    const { trackDownloadFilename, zipNumber } = this.state
    const result = []
    trackDownloadFilename.forEach(({ value }) => {
      if (value) {
        result.push(value)
      }
    })

    if (zipNumber) {
      result.unshift(ZIP_NUMBER)
    }

    return result
  }

  getZipNumber(): any {
    const uncompiledJson =
      this.props.user.business.private_metadata
        .track_download_filename_format_uncompiled_json || []
    const filenameFormat =
      this.props.user.business.private_metadata.track_download_filename_format
    return uncompiledJson.includes(ZIP_NUMBER) || !filenameFormat
  }

  getTrackDownloadFilename(): Array<TrackDownloadFilenameOption> {
    const allList = this._getTrackDownloadFilenameOptions()

    const uncompiledJson =
      this.props.user.business.private_metadata
        .track_download_filename_format_uncompiled_json || []
    const trackDownloadFilename = []
    uncompiledJson.forEach(value => {
      const findItem = allList.find(
        ({ value: listValue }) => listValue === value,
      )

      if (findItem) {
        trackDownloadFilename.push({
          value: findItem.value,
          label: findItem.label,
        })
      }
    })
    return trackDownloadFilename
  }

  getDefaultTrackDownloadFilename(): TrackDownloadFilenameOption[] {
    return [
      {
        label: 'Track Artist',
        value: 'track_artist',
      },
      {
        label: 'Track Title',
        value: 'track_title',
      },
    ]
  }

  UNSAFE_componentWillMount() {
    const trackDownloadFilename = this.getTrackDownloadFilename()
    const zipNumber = this.getZipNumber()
    this.setState({
      trackDownloadFilename: trackDownloadFilename.length
        ? trackDownloadFilename
        : this.getDefaultTrackDownloadFilename(),
      zipNumber,
    })
    this.props.loadBusinessSettings()
  }

  _getWraperLimitedOptions({
    isTrial,
    isFeatureEnabled,
  }: featureData): React.ReactNode | ((props: any) => React.ReactNode) {
    if (isTrial && !isFeatureEnabled) {
      return (props: Partial<featureData>) => (
        <Link
          {...props}
          className="BusinessSettings_UpgradeLink"
          to="/settings/billing/"
        />
      )
    }

    if (!isTrial && !isFeatureEnabled) {
      return (props: featureData) => (
        <ContactUsLink {...props} className="BusinessSettings_UpgradeLink" />
      )
    }

    return React.Fragment
  }

  _getComponentTranscoding: () => any | Array<any> = () => {
    const transcode_options = this.props.businessSettings?.transcode_options
    const featureData = this.props.featureData[TRANSCODE_320K]
    const isArtist = this.props.isArtist
    const isSubscriber = this.props.isSubscriber
    const isArtistTrial = !isSubscriber && isArtist
    const badge = this._getBadgeComponent(featureData)
    const showBadge = Boolean(badge) && !isArtistTrial
    return transcode_options
      ? transcode_options.map(({ value, key }) => {
          const isBadge = value === '320 kbps' && showBadge
          const disabled = isBadge && !featureData.isFeatureEnabled
          return {
            valueContainer:
              isBadge && this._getWraperLimitedOptions(featureData),
            value: key,
            label: value,
            badge: isBadge ? badge : undefined,
            disabled,
          }
        })
      : []
  }
  _getBadgeComponent: (arg0: featureData) => void | React.ReactNode = ({
    isFeatureEnabled,
    badgeTitle,
    isTrial,
  }: featureData) => {
    if (!isFeatureEnabled || isTrial) {
      return (
        <PricingBadge
          title={badgeTitle}
          description="This feature is available on Plus plans and above"
        />
      )
    }
  }
  _getTrackDownloadFilenameOptions: () => Array<
    | {
        badge: React.ReactNode
        disabled: boolean
        label: string
        value: string
        valueContainer: React.ReactNode
      }
    | {
        label: string
        value: string
      }
  > = () => {
    const featureData = this.props.featureData[CUSTOM_DOWNLOAD_FILE_NAMES]
    const isArtist = this.props.isArtist
    const isSubscriber = this.props.isSubscriber
    const isArtistTrial = !isSubscriber && isArtist
    const badge = this._getBadgeComponent(featureData)
    const isBadge: boolean = Boolean(badge) && !isArtistTrial
    const disabled = Boolean(isBadge && !featureData.isFeatureEnabled)
    return [
      {
        value: 'track_artist',
        label: 'Track Artist',
      },
      {
        value: 'track_title',
        label: 'Track Title',
      },
      {
        value: 'track_album',
        label: 'Track Album',
        badge: isBadge ? badge : undefined,
        disabled,
        valueContainer: isBadge
          ? this._getWraperLimitedOptions(featureData)
          : undefined,
      },
      {
        value: 'track_year',
        label: 'Track Year',
        badge: isBadge ? badge : undefined,
        disabled,
        valueContainer: isBadge
          ? this._getWraperLimitedOptions(featureData)
          : undefined,
      },
      {
        value: 'original_filename',
        label: 'Original filename',
        badge: isBadge ? badge : undefined,
        disabled,
        valueContainer: isBadge
          ? this._getWraperLimitedOptions(featureData)
          : undefined,
      },
      {
        value: 'business_name',
        label: 'Business name',
        badge: isBadge ? badge : undefined,
        disabled,
        valueContainer: isBadge
          ? this._getWraperLimitedOptions(featureData)
          : undefined,
      },
    ]
  }
  _onBusinessAddressChange: (
    val: any | null | undefined,
    valHtml: string | null | undefined,
    valMjml: string | null | undefined,
    valText: string | null | undefined,
  ) => void = (
    val: Record<string, any> | null | undefined,
    valHtml: string | null | undefined,
    valMjml: string | null | undefined,
    valText: string | null | undefined,
  ) => {
    let normalizedVal = val
    let normalizedValMjml = valMjml

    if (!(valText || '').trim()) {
      // trim address to trigger validation at another step
      normalizedVal = null
      normalizedValMjml = ''
    }

    this.setState({
      address: normalizedVal,
      addressMjml: normalizedValMjml,
    })
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open business settings tab')
  }

  _handleTrackDownloadFilenameBlur: () => void = () => {
    if (this.state.trackDownloadFilename.length === 0) {
      this.setState({
        trackDownloadFilename: this.getDefaultTrackDownloadFilename(),
      })
    }
  }
  _handleTracksDownloadFilenameChange: (
    options: Array<TrackDownloadFilenameOption>,
  ) => void = (options: TrackDownloadFilenameOption[]) => {
    const { isFeatureEnabled } =
      this.props.featureData[CUSTOM_DOWNLOAD_FILE_NAMES]

    if (isFeatureEnabled) {
      this.setState({
        trackDownloadFilename: options,
      })
    }
  }
  _onBusinessTypeChange: (option: DropdownStringOption) => void = (
    option: DropdownStringOption,
  ) => {
    const value = option?.value as BusinessTypes
    this.setState({
      businessType: value || null,
      invalidBusinessDataMessage: value
        ? undefined
        : 'Please select a business type',
    })
  }
  _handleTranscoding: (option: DropdownStringOption) => void = (
    option: DropdownStringOption,
  ) => {
    this.setState({
      transcoding: option.value,
    })
  }
  _handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      name: event.target.value,
    })
  }
  _handleFileDrop: (files: Array<File>) => void = (files: File[]) => {
    if (files[0].type.indexOf('image/') !== 0) return
    this.setState({
      imageFile: files[0],
    })
    const reader = new FileReader()

    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target?.result) {
        this.setState({
          image: e.target.result,
        })
      }
    }

    reader.readAsDataURL(files[0])
  }
  _removeImage: (e: React.SyntheticEvent<EventTarget>) => null | void = (
    e: React.SyntheticEvent<any>,
  ) => {
    const { user } = this.props
    e.preventDefault()
    e.stopPropagation()
    const isBusinessAdmin = user.permissions.includes(
      'can_change_business_settings',
    )
    if (!isBusinessAdmin) return null
    this.setState({
      image: null,
      imageFile: null,
    })
  }

  _getNewTrackDownloadFilenameFormat(): string {
    const { trackDownloadFilename, zipNumber } = this.state
    let result =
      trackDownloadFilename
        .map(({ value }) => `{${String(value)}}`)
        .join(' - ') || ''

    if (zipNumber) {
      result = `{${ZIP_NUMBER}} - ${result}`
    }

    return result
  }

  _handleBusinessSettingsSubmit: () => void = () => {
    let {
      address,
      addressMjml,
      attach_writers_to_metadata,
      auto_email_shared_playlist_passwords,
      explorer_trackhover_fields,
      explorer_trackpane_fields,
      imageFile,
      name,
      transcoding,
      write_native_tags_to_comments_metadata_enabled,
      businessType,
      invalidBusinessDataMessage,
    } = this.state
    if (invalidBusinessDataMessage) return undefined
    const { updateBusinessSettings } = this.props
    const { isFeatureEnabled } =
      this.props.featureData[CUSTOM_DOWNLOAD_FILE_NAMES]

    if (
      isEqual(
        explorer_trackhover_fields,
        getMultiLevelSetting.explorer_trackhover_fields(this.props.user)
          .settingBusiness,
      )
    ) {
      explorer_trackhover_fields = undefined
    }

    if (
      isEqual(
        explorer_trackpane_fields,
        getMultiLevelSetting.explorer_trackpane_fields(this.props.user)
          .settingDefault,
      )
    ) {
      explorer_trackpane_fields = undefined
    }

    const updateSetting = {
      name,
      business_address: address,
      image: imageFile,
      business_address_mjml: addressMjml,
      explorer_trackhover_fields,
      explorer_trackpane_fields,
      attach_writers_to_metadata,
      auto_email_shared_playlist_passwords,
      write_native_tags_to_comments_metadata_enabled,
      audio_transcode_preset: transcoding,
      private_metadata: isFeatureEnabled && {
        track_download_filename_format:
          this._getNewTrackDownloadFilenameFormat(),
        track_download_filename_format_uncompiled_json:
          this.getUncompiledJson(),
      },
      type_v2: businessType,
    }
    updateBusinessSettings(updateSetting)
  }

  _renderBackground(disabled: boolean): React.ReactElement<'div'> {
    if (this.state.image) {
      const backgroundStyle = {
        backgroundImage: `url("${this.state.image}")`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
      }
      let progressBar = null

      if (this.props.user.businessImageUploadProgress) {
        const progress = 100 - this.props.user.businessImageUploadProgress
        const progressStyle = {
          MozTransform: `translateX(-${progress}%)`,
          MsTransform: `translateX(-${progress}%)`,
          WebkitTransform: `translateX(-${progress}%)`,
          transform: `translateX(-${progress}%)`,
        }
        progressBar = (
          <div className="Form_Droparea_Progress">
            <div style={progressStyle} className="Form_Droparea_ProgressBar" />
          </div>
        )
      }

      let removeButton = null

      if (!progressBar && !disabled) {
        removeButton = (
          <div className="Form_ImageRemove" onClick={this._removeImage}>
            <Icon type="cancel" />
          </div>
        )
      }

      return (
        <div className="Form_Droparea_Background" style={backgroundStyle}>
          {removeButton}
          {progressBar}
        </div>
      )
    }

    return (
      <div className="Form_Upload">
        <CloudUpload
          color="#E2E2E2"
          style={{
            height: '4.2rem',
            width: '4.2rem',
            fontSize: 'inherit',
            margin: '0 auto',
            display: 'block',
          }}
        />
      </div>
    )
  }

  _renderLogoUploader(
    disabled: boolean,
  ): React.ReactElement<React.ComponentProps<any>, any> {
    return (
      <Row columnsSize={[12]} marginBottom="small">
        {disabled ? (
          <div className="Admin_DemoLogo">
            <Label caption="Logo" tooltipId="tooltip_settings_business_logo" />
            <div className="Admin_LogoDroparea">
              {this._renderBackground(disabled)}
              <div className="Admin_LogoHint">
                For best results, upload a square .png or .jpeg image
                at 200 × 200px
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Label caption="Logo" tooltipId="tooltip_settings_business_logo" />
            <FileDropWrapper onDrop={this._handleFileDrop}>
              <FileSelect
                className="Admin_LogoDroparea"
                onChange={this._handleFileDrop}
              >
                {this._renderBackground(disabled)}
                <div className="Admin_LogoHint">
                  For best results, upload a square .png or .jpeg image
                  at 200 × 200px
                </div>
              </FileSelect>
            </FileDropWrapper>
          </div>
        )}
      </Row>
    )
  }

  _renderDisabledAlert(): React.ReactNode {
    return (
      <Row columnsSize={[12]} marginBottom="small">
        <div className="Admin_DemoAlert">
          Business settings are only configurable by{' '}
          <Button
            appearance="inline-link"
            compact
            caption="business admins."
            link="/settings/users"
          />
        </div>
      </Row>
    )
  }

  render(): React.ReactNode {
    const { user } = this.props
    const { name, address, invalidBusinessDataMessage } = this.state
    const loadingBusiness = Boolean(user.businessUpdating)
    const isBusinessAdmin = user.permissions.includes(
      'can_change_business_settings',
    )
    const featureDataFileNames =
      this.props.featureData[CUSTOM_DOWNLOAD_FILE_NAMES]
    const disabled = !isBusinessAdmin
    return (
      <>
        <DocumentHeadTags title="Business Settings | Explorer | DISCO" />
        <PageHeader title="Business Settings" />
        <PageContainer>
          {disabled && this._renderDisabledAlert()}
          <div className="Admin_Section">
            <Row columnsSize={[12]} marginBottom="small">
              {this._renderLogoUploader(disabled)}
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <TextField
                  label="Name"
                  disabled={disabled}
                  value={name}
                  onChange={this._handleNameChange}
                  tooltipId="tooltip_settings_business_name"
                />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Business address"
                  tooltipId="tooltip_settings_business_address"
                />
                <RichTextEditor
                  readOnly={disabled}
                  className="CampaignDetails_Input"
                  initialContent={address}
                  onChange={this._onBusinessAddressChange}
                />
              </div>
            </Row>

            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label caption="What describes you or your workplace?" />
                <CustomDropdown
                  disabled={disabled}
                  options={BUSINESS_TYPES.map(value => ({
                    value,
                    label: BUSINESS_TYPES_LABEL_MAP[value],
                  }))}
                  value={this.state.businessType}
                  onChange={this._onBusinessTypeChange}
                />
                {invalidBusinessDataMessage && (
                  <InlineError error={invalidBusinessDataMessage} />
                )}
              </div>
            </Row>

            <MetadataHoverSettings
              disabled={disabled}
              value={{
                hover: this.state.explorer_trackhover_fields,
                pane: this.state.explorer_trackpane_fields,
              }}
              onChange={value => {
                this.setState({
                  explorer_trackhover_fields: value.hover,
                  explorer_trackpane_fields: value.pane,
                })
              }}
            />

            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Label
                  caption="Transcoding quality"
                  tooltipContent="If you upload WAVs or AIFFs, DISCO will create an mp3 copy. This setting controls the bit rate of those mp3s. Higher bit rates have better sound quality, but are also bigger files, so can take longer for recipients to stream and download."
                />
                <CustomDropdown
                  disabled={disabled}
                  value={this.state.transcoding}
                  options={this._getComponentTranscoding()}
                  onChange={this._handleTranscoding}
                />
              </div>
            </Row>

            <React.Fragment>
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <Label
                    caption="Track download filenames"
                    tooltipContent="Here you can customise the filenames of your downloaded tracks. By default, they download as “Artist – Title”, for eg. “Pavement – Grounded”."
                  />
                  <CustomDropdown
                    disabled={disabled}
                    multi
                    value={
                      featureDataFileNames.isFeatureEnabled
                        ? this.state.trackDownloadFilename
                        : this.getDefaultTrackDownloadFilename()
                    }
                    options={this._getTrackDownloadFilenameOptions()}
                    onBlur={this._handleTrackDownloadFilenameBlur}
                    onChange={this._handleTracksDownloadFilenameChange}
                    disabledRemoveTag={!featureDataFileNames.isFeatureEnabled}
                    className={cn({
                      'is-disabled': !featureDataFileNames.isFeatureEnabled,
                    })}
                  />
                </div>
              </Row>
            </React.Fragment>
            {featureDataFileNames.isFeatureEnabled && (
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <Checkbox
                    disabled={disabled}
                    checked={this.state.zipNumber}
                    onChange={(_, v) => {
                      this.setState({
                        zipNumber: v,
                      })
                    }}
                  >
                    Include order number prefix on track filenames
                    <HelpTooltip>
                      By default, an order number is added to track filenames
                      for tracks downloaded in playlist, i.e. &quot;01 Pavement
                      – Grounded&quot;, &quot;02 Guided by Voices – Smothered In
                      Hugs&quot;. This helps keep the tracks in order once
                      they’re downloaded to your recipient’s file system. If
                      you’d like the tracks to download without these order
                      numbers, uncheck this option.
                    </HelpTooltip>
                  </Checkbox>
                </div>
              </Row>
            )}

            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Checkbox
                  disabled={disabled}
                  checked={
                    this.state.write_native_tags_to_comments_metadata_enabled
                  }
                  onChange={(_, v) => {
                    this.setState({
                      write_native_tags_to_comments_metadata_enabled: v,
                    })
                  }}
                >
                  Write native tags to Comments metadata field on downloaded
                  tracks
                </Checkbox>
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <Checkbox
                  disabled={disabled}
                  checked={this.state.attach_writers_to_metadata}
                  onChange={(_, v) => {
                    this.setState({
                      attach_writers_to_metadata: v,
                    })
                  }}
                >
                  Include writer split info from Writers tab in Composer field
                  on downloaded tracks
                </Checkbox>
              </div>
            </Row>
            <Row columnsSize={[12]}>
              <div>
                <Checkbox
                  disabled={disabled}
                  checked={this.state.auto_email_shared_playlist_passwords}
                  onChange={(_, v) => {
                    this.setState({
                      auto_email_shared_playlist_passwords: v,
                    })
                  }}
                >
                  Automatically include passwords in playlist share emails
                </Checkbox>
              </div>
            </Row>
            <Row columnsSize={[12]}>
              <div>
                <Button
                  disabled={disabled || Boolean(invalidBusinessDataMessage)}
                  appearance="primary"
                  caption="Update Business Settings"
                  onClick={this._handleBusinessSettingsSubmit}
                  progressCaption="Updating Settings"
                  progressIndicator={loadingBusiness}
                />
              </div>
            </Row>
          </div>
        </PageContainer>
      </>
    )
  }
}

export default connector(BusinessSettings)
